<template>
  <div class="pageContainer">
    <BannerComponent>
      <h6 class="banner-heading">Current Metrics & Stats</h6>
    </BannerComponent>
    <div class="card cardBackground">
      <div class="cardContentWrapper" v-for="network of networks" v-bind:key="network.name">
        <div class="cardLine">
          <p>
            {{network.name}}
          </p>
          <font-awesome-icon @click="openInfo(network)" class="infoIcon" :icon="['fas', 'circle-info']" />
        </div>
        <div class="lineDiv" />
      </div>
    </div>
    <div class="card cardBackground" v-for="network of networks" v-bind:key="network.name">
      <div class="chartHeader">
        <h4>{{ network.name }} Usage Over Time</h4>
      </div>
      <LineChart
          :datasets="network.displayData"
          :labels="network.labels"
          :options="network.chartOptions"
          style="height: 500px;"
      ></LineChart>
    </div>

    <div class="card cardBackground">
      <div class="chartHeader">
        <h4>ISP Gateway Packet Loss</h4>
      </div>
      <LineChart
          :datasets="gatewayData"
          :labels="gatewayLabels"
          :options="gatewayChartOptions"
          style="height: 400px;"
      ></LineChart>
    </div>
    <BaseModal
        :visible="modals.info"
        :submit-show="false"
        :cancel-show="false"
        @close="closeInfo"
        title="Network Stats"
    >
      <div class="modalContainer">
        <div class="modalLine">
          <p>Name:</p>
          <p style="margin-left: 15px">{{selectedNetwork.name}}</p>
        </div>
        <div class="modalLine">
          <p>Speed:</p>
          <p style="margin-left: 15px">{{selectedNetwork.speed}} Mbps</p>
        </div>
        <div class="modalLine">
          <p>Inbound:</p>
          <p style="margin-left: 15px">{{selectedNetwork.inbound}} MB</p>
        </div>
        <div class="modalLine">
          <p>Outbound:</p>
          <p style="margin-left: 15px">{{selectedNetwork.outbound}} MB</p>
        </div>
      </div>
    </BaseModal>
    <MessageModal :message="appMessage.text" :type="appMessage.type" ref="messageModal" />
    <LoadingPage :is-visible="loading"/>
  </div>
</template>

<script>
import MessageModal from "@/components/MessageModal.vue";
import BannerComponent from "@/components/BannerComponent.vue";
import BaseModal from "@/components/BaseModal.vue";
import LineChart from "@/components/LineChart.vue";
import dayjs from "dayjs";
import DeviceAPIService from "@/servicehandlers/DeviceAPIService";
import {useLoginStore} from "@/stores/loginStore";
import LoadingPage from "@/components/LoadingPage.vue";
const deviceAPIService = new DeviceAPIService()

function getColorRGB(pct) {
  // Clamp to [0,100]
  pct = Math.min(Math.max(pct, 0), 100);
  const t = pct / 100; // normalized 0→1

  let r, g, b;

  if (t <= 0.5) {
    // 0 → 0.5: red → yellow
    // u goes 0→1 as t goes 0→0.5
    const u = t / 0.5;
    r = 255;                     // stays at 255 (red channel)
    g = Math.round(255 * u);     // goes 0→255
    b = 0;
  } else {
    // 0.5 → 1: yellow → green
    // u goes 0→1 as t goes 0.5→1
    const u = (t - 0.5) / 0.5;
    r = Math.round(255 * (1 - u)); // goes 255→0
    g = 255;                       // stays at 255 (green channel)
    b = 0;
  }

  // Helper to convert 0–255 → two-digit hex
  const toHex = (v) => {
    const h = v.toString(16);
    return h.length === 1 ? "0" + h : h;
  };

  return "#" + toHex(r) + toHex(g) + toHex(b);
}
function getPercentageOfMinMax(value, options){
  let range = options.scales.y.max  - options.scales.y.min
  return ((value - options.scales.y.min) / range) * 100
}

export default {
  name: 'NetworkStatsPage',
  components: {LoadingPage, LineChart, BaseModal, BannerComponent, MessageModal},
  data(){
    return {
      loading: false,
      appMessage: {
        text: '',
        type: 'error',
      },
      networks: [],
      selectedNetwork: {},
      modals: {
        info: false,
      },
      labelFormat: 'HH:mm',
      possibleBorderColors: ['#63758B', '#0f9994', '#105891', '#d32d2d', '#49a705'],
      gatewayLabels: [],
      gatewayData: [],
      gatewayChartOptions: {
        plugins: {
          legend: { display: false },
          tooltip: { mode: 'nearest' },
          /*datalabels: {
            color: '#63758B',
            font: { size: 14, weight: 'bold' },
            formatter: (value) => value.toString(),
            anchor: 'end',
            align: 'top'
          }*/
        },
        scales: {
          y: {
            min: 0,
            max: 10,
            title: {
              display: false,
            }
          },
          x: {
            title: {
              display: false,
            }
          }
        },
        elements: {
          point: {
            borderWidth: 0
          }
        }
      }
    }
  },
  async mounted(){
    //console.clear()
    // Only 'device' users can access network statistics functionality
    if (useLoginStore().getSubscriptionType === 'device') {
      if (useLoginStore().getDeviceOnline) {
        this.loading = true
        await this.getNetworks()
        this.loading = false
      } else {
        this.appMessage.text = "Device Offline"
        this.appMessage.type = 'info'
        this.$refs.messageModal.open()
      }
    }
    // 'free' users handled by route guards/subscription service
  },
  methods: {
    async getNetworks(){
      //let last24 = dayjs().add(-1, 'day')

      let metricData = await deviceAPIService.networkMetrics(this.$router, useLoginStore())

      for (let key of Object.keys(metricData.metrics)){
        let temp = {
              name: key === 'br0' ? 'WAN' : 'LAN',
              speed: metricData.metrics[key].speed,
              inbound: 10,
              outbound: 20,
              data: {
                //speed: [],
                inbound: [],
                outbound: []
              },
              displayData: [],
              labels: [],
              chartOptions: {
                plugins: {
                  legend: { display: false },
                  tooltip: { mode: 'nearest' },
                  /*datalabels: {
                    color: '#63758B',
                    font: { size: 14, weight: 'bold' },
                    formatter: (value) => value.toString(),
                    anchor: 'end',
                    align: 'top'
                  }*/
                },
                scales: {
                  y: {
                    min: 0,
                    max: 10,
                    title: {
                      display: false,
                    }
                  },
                  x: {
                    title: {
                      display: false,
                    }
                  }
                },
                elements: {
                  point: {
                    borderWidth: 0
                  }
                }
              },
            }

        //let history = metricData.history.filter(entry => entry.iface === key)

        for (let stat of metricData.stats){
          let inboundStat = {
            time: dayjs(stat.timestamp),
            value: parseFloat(stat.stats[key].inbound.split(' ')[0]),
          }
          let outboundStat = {
            time: dayjs(stat.timestamp),
            value: parseFloat(stat.stats[key].outbound.split(' ')[0]),
          }

          temp.data.inbound.push(inboundStat)
          temp.data.outbound.push(outboundStat)
        }

        this.networks.push(temp)
      }

      for (let network of this.networks){

        if (network.data.length > 1){
          network.chartOptions.elements = {
            point: {
              borderWidth: 2
            }
          }
        }

        //network.chartOptions.plugins.datalabels = null

        let allTimes = [];
        for (let key of Object.keys(network.data)){
          for (let { time } of network.data[key]) {
            allTimes.push(time);
          }
        }

        const uniqueSorted = Array.from(
            new Set(allTimes.map(d => d.valueOf()))
        )
            .sort((a, b) => a - b)
            .map(ms => dayjs(ms));

        network.labels = uniqueSorted.map(d => d.format(this.labelFormat));

        const allPoints = Object
            .values(network.data)
            .flat();

        const allValues = allPoints.map(pt => pt.value);

        const highest = Math.max(...allValues);

        const lowest = Math.min(...allValues)

        network.chartOptions.scales.y.max = highest + Math.ceil((highest - lowest) * .05)

        network.chartOptions.scales.y.min = lowest - Math.floor((highest - lowest) * .25)

        if (network.chartOptions.scales.y.max === 0){
          network.chartOptions.scales.y.max = 5
        }

        if (network.chartOptions.scales.y.min === 0){
          network.chartOptions.scales.y.min = -1
        }

        network.displayData = []

        for (let key of Object.keys(network.data)) {

          let temp = {
            label: network.name + ' ' + key,
            data: [],
            backgroundColor: [],
            borderColor: this.possibleBorderColors[Object.keys(network.data).indexOf(key) % 5],
            borderWidth: 1,
            pointBorderWidth: 0,
            pointBorderColor: 'transparent',
            pointRadius: 2,
            tension: 0
          };

          for (let label of network.labels) {
            const match = network.data[key].find(item =>
                item.time.format(this.labelFormat) === label
            );

            if (match) {
              temp.data.push(match.value);
              temp.backgroundColor.push(
                  getColorRGB(getPercentageOfMinMax(match.value, network.chartOptions))
              );
            } else {
              temp.data.push(null);
              temp.backgroundColor.push(null);
            }
          }

          network.displayData.push(temp);
        }
      }
      this.getGatewayData(metricData.gateway)
    },
    getGatewayData(metricData){
      let gatewayData = {
          name: 'Gateway',
          data: [],
        };

      for (let entry of metricData){
        let entryData = {
          value: parseFloat(entry.packetLoss),
          time: dayjs(entry.timestamp)
        }
        gatewayData.data.push(entryData)
      }

      console.log(gatewayData)

      let allTimes = [];
      for (let { time } of gatewayData.data) {
        allTimes.push(time);
      }

      const uniqueSorted = Array.from(
          new Set(allTimes.map(d => d.valueOf()))
      )
          .sort((a, b) => a - b)
          .map(ms => dayjs(ms));

      this.gatewayLabels = uniqueSorted.map(d => d.format(this.labelFormat));

      const allValues = gatewayData.data.map(pt => pt.value);

      const highest = Math.max(...allValues);

      const lowest = Math.min(...allValues)

      this.gatewayChartOptions.scales.y.max = highest + Math.ceil((highest - lowest) * .05)

      this.gatewayChartOptions.scales.y.min = lowest - Math.floor((highest - lowest) * .25)

      if (this.gatewayChartOptions.scales.y.max === 0){
        this.gatewayChartOptions.scales.y.max = 5
      }

      if (this.gatewayChartOptions.scales.y.min === 0){
        this.gatewayChartOptions.scales.y.min = -1
      }

      let temp = {
        label: 'Packet Loss',
        data: [],
        backgroundColor: [],
        borderColor: this.possibleBorderColors[0],
        borderWidth: 1,
        pointBorderWidth: 0,
        pointBorderColor: 'transparent',
        pointRadius: 2,
        tension: 0
      };

      for (let label of this.gatewayLabels) {
        // Try to find a matching testData item whose time.format(...) === label
        const match = gatewayData.data.find(item =>
            item.time.format(this.labelFormat) === label
        );

        if (match) {
          // Found one, push its value and compute color
          temp.data.push(match.value);
          temp.backgroundColor.push(
              getColorRGB(getPercentageOfMinMax(match.value, this.gatewayChartOptions))
          );
        } else {
          // No testData entry for this label → push exactly one null
          temp.data.push(null);
          temp.backgroundColor.push(null);
        }
      }

      this.gatewayData.push(temp);
    },
    openInfo(network){
      this.selectedNetwork = network
      this.modals.info = true
    },
    closeInfo(){
      this.selectedNetwork = {}
      this.modals.info = false
    }
  },
}
</script>

<style scoped>
</style>
